import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import { Viewer, Worker } from '@react-pdf-viewer/core';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';

import { Button } from 'components';

import {
	ArrowUpIcon,
	ArrowDownIcon,
	MinusIcon,
	PlusIcon,
	PrintIcon,
	DownloadIcon,
	FullscreenIcon,
} from '../../icons';

import styles from './pdf-viewer.module.scss';
import { useStaticShared } from '../../hooks';
import { getProperty } from '../../utils';

const renderToolbarButton =
	(Icon) =>
	({ isDisabled, onClick }) =>
		(
			<button
				onClick={onClick}
				className={styles.button}
				{...(isDisabled ? { disabled: true } : {})}>
				<Icon className={styles.icon} />
			</button>
		);

const ProgressBar = ({ numLoadedPages, numPages, onCancel }) => {
	const staticShared = useStaticShared();

	const page = getProperty(staticShared, 'data.meta.0.page');
	const loading = getProperty(staticShared, 'data.misc.0.loading');
	const cancel = getProperty(staticShared, 'data.buttons.0.cancel');

	return (
		<div className={styles.progressbar}>
			<div className={styles.container}>
				<div className={styles.label}>
					{loading} {page} {numLoadedPages}/{numPages}
				</div>
				<Button type="minimal" color="tertiary" onClick={onCancel}>
					{cancel}
				</Button>
			</div>
		</div>
	);
};

export const PDFViewer = ({ file }) => {
	const toolbarPluginInstance = toolbarPlugin({
		getFilePlugin: {
			fileNameGenerator: () => file.name,
		},
		printPlugin: {
			renderProgressBar: (numLoadedPages, numPages, onCancel) => (
				<ProgressBar
					onCancel={onCancel}
					numPages={numPages}
					numLoadedPages={numLoadedPages}
				/>
			),
		},
	});
	const [isLoading, setIsLoading] = useState(true);

	return (
		<div className={[styles.wrapper, isLoading ? styles.loading : ''].join(' ')}>
			<Worker workerUrl="/pdf.worker.min.js">
				<div className={styles.toolbar}>
					<toolbarPluginInstance.Toolbar>
						{({
							CurrentPageLabel,
							Download,
							EnterFullScreen,
							GoToNextPage,
							GoToPreviousPage,
							NumberOfPages,
							Print,
							CurrentScale,
							ZoomIn,
							ZoomOut,
						}) => {
							return (
								<Fragment>
									<div className={styles.pagination}>
										<GoToPreviousPage>
											{renderToolbarButton(ArrowUpIcon)}
										</GoToPreviousPage>
										<GoToNextPage>{renderToolbarButton(ArrowDownIcon)}</GoToNextPage>

										<span className={styles.label}>
											<CurrentPageLabel /> / <NumberOfPages />
										</span>
									</div>

									<div className={styles.zoom}>
										<ZoomOut>{renderToolbarButton(MinusIcon)}</ZoomOut>
										<ZoomIn>{renderToolbarButton(PlusIcon)}</ZoomIn>

										<CurrentScale>
											{({ scale }) => (
												<span className={styles.scale}>{`${Math.round(
													scale * 100
												)}%`}</span>
											)}
										</CurrentScale>
									</div>

									<div className={styles.actions}>
										<EnterFullScreen>
											{renderToolbarButton(FullscreenIcon)}
										</EnterFullScreen>
										<Download>{renderToolbarButton(DownloadIcon)}</Download>
										<Print>{renderToolbarButton(PrintIcon)}</Print>
									</div>
								</Fragment>
							);
						}}
					</toolbarPluginInstance.Toolbar>
				</div>
				<div className={styles.viewer}>
					<Viewer
						defaultScale={1}
						fileUrl={file.url}
						plugins={[toolbarPluginInstance]}
						onDocumentLoad={() => setIsLoading(false)}
					/>
				</div>
			</Worker>
		</div>
	);
};

PDFViewer.propTypes = {
	file: PropTypes.shape({
		url: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
	}).isRequired,
};
